import { CustomDatePickerErrorStrings, CustomTimePickerErrorStrings } from "../Date/M365PulseDatePickerPill.types";
import { DateTime, DateTimeUnit, ToRelativeUnit } from "luxon";
import { roundDownToNearest, roundUpToNearest } from "Shared/Utils";

import { M365FilterBaseProps } from "./M365FilterPill.types";
import { ValueSyncPattern } from "./useM365FilterPillControl.types";

export interface Duration {
    startTime?: string;
    endTime?: string;
    text?: string;
}

export interface DurationOption {
    key: string;
    text?: string;
    getText?: (selectedDuration: Duration) => string;
    getDuration: () => Duration;
}

export interface DurationPickerPillProps extends M365FilterBaseProps {
    /**
     * The title before the selected value
     * @default "Duration"
     */
    title?: string;
    /**
     * Global Id of filter for pill key
     */
    globalId?: string;
    /**
     * Whether or not there is a custom option in the dropdown
     * @default true
     */
    custom?: boolean;
    /**
     * The farthest back the duration picker can go
     * @default 90
     */
    oldestDays?: number;
    /**
     * The maximum length between the from and to date
     */
    maxDays?: number;

    /**
     * The maximum length between the from and to time in hours
     * If both maxDays and maxHours are provided, maxHours takes precedence
     */
    maxHours?: number;

    /*
     * The default duration option key.
     */
    defaultDurationOptionKey?: string;

    /**
     * The duration option key used in controlled component.
     * if the key is custom, use getCustomKeyForDuration to generate the key.
     */
    durationOptionKey?: string;

    /**
     * Callback to receive the start and end time changes
     */
    onChange?: (durationOption: DurationOption) => void;
    /**
     * The duration options to be displayed by the picker
     */
    durationOptions: DurationOption[];
    /**
     * Enable time picker or not
     */
    enableTimePicker?: boolean;

    /**
     * Defines whether the selected value should be synced with url, setting or both
     * @default both
     */
    selectedValueSyncType?: ValueSyncPattern;

    /**
     * Function to customize the string rendered in FilterPill.
     * @param time
     */
    customFormat?: (time?: string | Date | number | DateTime | null | undefined) => string;
}

export const OneHour: DurationOption = getDurationOption(1, "hour");
export const OneHourRoundDown: DurationOption = getDurationOption(1, "hour", "roundDown");
export const TwoHours: DurationOption = getDurationOption(2, "hours");
export const TwoHoursRoundDown: DurationOption = getDurationOption(2, "hours", "roundDown");
export const ThreeHours: DurationOption = getDurationOption(3, "hours");
export const ThreeHoursRoundDown: DurationOption = getDurationOption(3, "hours", "roundDown");
export const FourHours: DurationOption = getDurationOption(4, "hours");
export const FourHoursRoundDown: DurationOption = getDurationOption(4, "hours", "roundDown");
export const SixHours: DurationOption = getDurationOption(6, "hours");
export const SixHoursRoundDown: DurationOption = getDurationOption(6, "hours", "roundDown");
export const EightHours: DurationOption = getDurationOption(8, "hours");
export const EightHoursRoundDown: DurationOption = getDurationOption(8, "hours", "roundDown");
export const OneDay: DurationOption = getDurationOption(1, "day");
export const OneDayRoundDown: DurationOption = getDurationOption(1, "day", "roundDown");
export const SevenDays: DurationOption = getDurationOption(7, "days");
export const FourteenDays: DurationOption = getDurationOption(14, "days");
export const ThirtyDays: DurationOption = getDurationOption(30, "days");
export const TwoWeeks: DurationOption = getDurationOption(2, "weeks");
export const OneMonth: DurationOption = getDurationOption(1, "month");
export const ThreeMonths: DurationOption = getDurationOption(3, "months");
export const SixMonths: DurationOption = getDurationOption(6, "months");
export const OneYear: DurationOption = getDurationOption(1, "year");
export const AllTime: DurationOption = { key: "All", text: "All Time", getDuration: () => ({ startTime: "", endTime: "" }) };

export const DEFAULT_DURATION_OPTIONS = [OneDay, SevenDays, FourteenDays, ThirtyDays] as const;
export const DEFAULT_DURATION_TITLE = "Duration";

export function getDurationOption(amount: number, unit: DateTimeUnit | ToRelativeUnit, roundKind: "roundUp" | "roundDown" = "roundUp"): DurationOption {
    return {
        key: `${amount}${unit}`,
        text: `Last ${amount} ${unit}`,
        getDuration: () => {
            const now = roundKind === "roundUp" ? roundUpToNearest(DateTime.utc(), 1, "minute") : roundDownToNearest(DateTime.utc(), 1, "minute");
            return { startTime: now.minus({ [unit]: amount }).toISO(), endTime: now.toISO() };
        }
    };
}

export const NextOneMonth: DurationOption = getFutureDurationOption(1, "month");
export const NextThreeMonths: DurationOption = getFutureDurationOption(3, "months");
export const NextSixMonths: DurationOption = getFutureDurationOption(6, "months");

export const DEFAULT_FUTURE_DURATION_TITLE = "Period";

export function getFutureDurationOption(amount: number, unit: DateTimeUnit | ToRelativeUnit, roundKind: "roundUp" | "roundDown" = "roundUp"): DurationOption {
    return {
        key: `${amount}${unit}`,
        text: `Next ${amount} ${unit}`,
        getDuration: () => {
            const now = roundKind === "roundUp" ? roundUpToNearest(DateTime.utc(), 1, "minute") : roundDownToNearest(DateTime.utc(), 1, "minute");
            return { startTime: now.toISO(), endTime: now.plus({ [unit]: amount }).toISO() };
        }
    };
}

export function getMaxDateErrorMessage(maxDays: number): CustomDatePickerErrorStrings {
    return { "out-of-bounds": `Date is out of the range. Max Duration is ${maxDays} days.` };
}

export function getMaxTimeErrorMessage(maxHours: number): CustomTimePickerErrorStrings {
    return { "out-of-bounds": `Time is out of the range. Max Duration is ${maxHours} hours.` };
}
